import { useCallback, useEffect, useRef, useState } from 'react';

import {
  listSpaces, createSpace, updateSpacePolicy, listVenueDirectory,
  type Space, type DirectoryVenue, type OccupancyMode,
} from '../../lib/tenancy';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useAdminScope } from '../../admin/scope';
import { useCopy } from '../../copy/CopyProvider';
import './AdminPages.css';

/**
 * Spaces — the physical unit between a Venue and an Event.
 *
 * ── WHY THIS SCREEN IS SMALL ─────────────────────────────────────────────────────────
 *
 * Task 6 built the Space model, its policies and its API, and deliberately shipped no UI.
 * This is the minimum that makes the new "Spaces" nav item true: see every Space, the
 * venue and organization it belongs to, its capacity and status, and its policy — plus
 * the two mutations an operator actually needs on day one, which are creating a Space and
 * changing its policy. Renaming, deactivating and the rental-space assignment UI are
 * deliberately absent rather than half-built.
 *
 * ── IDS, NEVER NAMES ─────────────────────────────────────────────────────────────────
 *
 * A Space is created against a (venueKind, venueId) pair taken from the venue directory.
 * Nothing on this screen matches a venue by name, and the venue picker submits the id it
 * was given — the same rule the engine enforces one layer down.
 *
 * ── POLICY DEFAULTS PRESERVE BEHAVIOUR ───────────────────────────────────────────────
 *
 * 'Admissions only' means credentials do not consume public ticket inventory, and
 * 'Requires Voleto admissions' is off. Those are the values every backfilled Space
 * already carries; changing them here changes a commercial rule, so the control says what
 * it does rather than showing a bare toggle.
 */

const OCCUPANCY_LABEL: Record<OccupancyMode, string> = {
  admissions_only: 'Admissions only',
  physical_occupancy: 'Physical occupancy',
};

function PolicyEditor({ space, onChanged }: { space: Space; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const policy = space.policy;

  if (!policy) return null;

  async function save(patch: Parameters<typeof updateSpacePolicy>[1]) {
    setBusy(true);
    setError(null);
    try {
      await updateSpacePolicy(space.id, patch);
      onChanged();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not update the policy.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="spc-policy">
      {error && <p className="alert alert--error" role="alert">{error}</p>}

      <label className="spc-policy__row">
        <input
          type="checkbox"
          checked={policy.publicEventRequiresVoleto}
          disabled={busy}
          onChange={(e) => void save({ publicEventRequiresVoleto: e.target.checked })}
        />
        <span>
          Public events here must issue Voleto admissions
          <small className="spc-policy__hint">
            Off by default. Turning it on changes what publishing means for this room.
          </small>
        </span>
      </label>

      <label className="spc-policy__row">
        <input
          type="checkbox"
          checked={policy.allowPrivateEventExemption}
          disabled={busy || !policy.publicEventRequiresVoleto}
          onChange={(e) => void save({ allowPrivateEventExemption: e.target.checked })}
        />
        <span>
          Private events may be exempt
          <small className="spc-policy__hint">
            Only meaningful while the rule above is on.
          </small>
        </span>
      </label>

      <label className="field spc-policy__field">
        <span className="field__label">Occupancy mode</span>
        <select
          className="field__input"
          value={policy.occupancyMode}
          disabled={busy}
          onChange={(e) => void save({ occupancyMode: e.target.value as OccupancyMode })}
        >
          {(Object.keys(OCCUPANCY_LABEL) as OccupancyMode[]).map((mode) => (
            <option key={mode} value={mode}>{OCCUPANCY_LABEL[mode]}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

function CreateSpace({ venues, onCreated }: { venues: DirectoryVenue[]; onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [venueKey, setVenueKey] = useState('');
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const venue = venues.find((v) => `${v.venueKind}:${v.venueId}` === venueKey);
    if (!venue || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createSpace({
        venueKind: venue.venueKind,
        venueId: venue.venueId,
        name: name.trim(),
        capacity: capacity.trim() ? Number(capacity) : null,
      });
      setName(''); setCapacity(''); setOpen(false);
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not create the space.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="button button--secondary" onClick={() => setOpen(true)}>
        + Add a space
      </button>
    );
  }

  return (
    <section className="net-panel">
      <h2 className="net-panel__title">New space</h2>
      {error && <p className="alert alert--error" role="alert">{error}</p>}
      <div className="wiz-grid">
        <label className="field">
          <span className="field__label">Venue</span>
          <select className="field__input" value={venueKey}
            onChange={(e) => setVenueKey(e.target.value)}>
            <option value="">Choose a venue…</option>
            {venues.map((v) => (
              <option key={`${v.venueKind}:${v.venueId}`} value={`${v.venueKind}:${v.venueId}`}>
                {v.name} ({v.venueKind === 'managed_venue' ? 'managed' : 'self-listed'})
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Name</span>
          <input className="field__input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Main Theater" />
        </label>
        <label className="field">
          <span className="field__label">Capacity (optional)</span>
          <input className="field__input" type="number" min="1" value={capacity}
            onChange={(e) => setCapacity(e.target.value)} />
        </label>
      </div>
      <div className="spc-actions">
        <button type="button" className="button button--primary"
          disabled={busy || !venueKey || !name.trim()} onClick={() => void submit()}>
          Create space
        </button>
        <button type="button" className="button button--ghost" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </section>
  );
}

export function AdminSpaces() {
  const t = useCopy();
  const { organizationId } = useAdminScope();
  const [spaces, setSpaces] = useState<Space[] | null>(null);
  const [venues, setVenues] = useState<DirectoryVenue[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Scope comes from the URL, and an absent organization means every organization.
  //
  // THE GUARD IS NOT DECORATION. On first load the URL may not carry ?org= yet — a
  // remembered default seeds it a tick later — so two reads are in flight: the unscoped
  // one and the scoped one. Without a token the unscoped response can land second and
  // overwrite the scoped list, leaving the selector saying one organization while the
  // rows below belong to another.
  const requestToken = useRef(0);

  const load = useCallback(() => {
    const token = ++requestToken.current;
    const fresh = () => token === requestToken.current;

    listSpaces({ organizationId })
      .then((rows) => { if (fresh()) setSpaces(rows); })
      .catch((err: unknown) => {
        if (fresh()) setError(err instanceof ApiError ? err.message : 'Could not load spaces.');
      });
    listVenueDirectory({ organizationId })
      .then((rows) => { if (fresh()) setVenues(rows); })
      .catch(() => { if (fresh()) setVenues([]); });
  }, [organizationId, t]);

  useEffect(load, [load]);

  usePageChrome({
    title: t('admin.spaces.title', 'Spaces'),
    subtitle: organizationId
      ? 'Rooms in the selected organization, with the policy each one carries.'
      : 'Every room across every organization, with the policy each one carries.',
  }, [organizationId, t]);

  const venueLabel = (space: Space) =>
    space.venueName
      ?? venues.find((v) => v.venueId === space.venueId)?.name
      ?? 'Unknown venue';

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <CreateSpace venues={venues} onCreated={load} />

      {!spaces && !error && <p className="adm-lede">Loading spaces…</p>}
      {spaces && spaces.length === 0 && (
        <p className="adm-lede">
          No spaces {organizationId ? 'in this organization' : 'yet'}. A space is the room an
          event happens in, and it is what links an event to its venue and organization
          without matching names.
        </p>
      )}

      <div className="adm-list">
        {(spaces ?? []).map((space) => (
          <article className="net-panel" key={space.id}>
            <div className="spc-head">
              <div>
                <h2 className="net-panel__title">{space.name}</h2>
                <p className="net-panel__lede">
                  {venueLabel(space)}
                  {' · '}
                  {space.venueKind === 'managed_venue' ? 'Managed venue' : 'Self-listed venue'}
                </p>
              </div>
              <div className="net-row__badges">
                <span className={`badge ${space.status === 'active' ? 'badge--ok' : 'badge--muted'}`}>
                  {space.status}
                </span>
                <span className="badge badge--muted">
                  {space.capacity != null ? `${space.capacity} capacity` : 'Capacity not set'}
                </span>
                <span className="badge badge--muted">
                  {space.policy ? OCCUPANCY_LABEL[space.policy.occupancyMode] : 'No policy'}
                </span>
                {space.organizationId == null && (
                  <span className="badge badge--pending">No organization</span>
                )}
              </div>
            </div>

            <PolicyEditor space={space} onChanged={load} />
          </article>
        ))}
      </div>
    </div>
  );
}
